import { useAtom, useSetAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import AlertModal from '@/components/Common/Modal/AlertModal';
import PhoneAuthAndTerms from '@/components/SignUp/PhoneAuthAndTerms';
import UserRegistrationForm from '@/components/SignUp/UserRegistrationForm';
import { DEALER_BLACK_LIST_MEMBER } from '@/const/errorCode';
import { DEALER_BLACK_LIST_MEMBER_MESSAGE } from '@/const/errorMessage';
import { initialSignUpFormState, signUpFormAtom } from '@/store/signUp';

const SignUp = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const signUpType = (location.state as { signUpType?: string })?.signUpType;
  const isDealerSignUp = signUpType === 'DEALER';
  const code = searchParams.get('code');
  const errorCode = searchParams.get('errorCode');
  const navigate = useNavigate();

  const [isFirst, setIsFirst] = useState(true);
  const [isShowDealerBlackListPopup, setIsShowDealerBlackListPopup] = useState(false);
  const [signUpFormData] = useAtom(signUpFormAtom);
  const setSignUpFormData = useSetAtom(signUpFormAtom);

  const [isShowPopup, setIsShowPopup] = useState(false);

  const [memberInfo, setMemberInfo] = useState<MemberInfo>({
    marketing: false,
    terms: false,
    personalInfo: false,
    name: '',
    password: '',
    phoneNumber: '',
    authNumber: '',
    isSentCode: false,
    isAuthenticateCode: false,
  });

  const updateMemberInfo = (data: MemberInfo) => {
    setSignUpFormData({
      ...data,
      signUpType: isDealerSignUp ? 'DEALER' : 'NORMAL',
    });
  };

  // 탭 전환 시 폼 초기화, 같은 탭 내에서는 signUpFormData 동기화 가능하도록
  useEffect(() => {
    const currentSignUpType = isDealerSignUp ? 'DEALER' : 'NORMAL';
    const storedSignUpType = signUpFormData.signUpType ?? 'NORMAL';

    if (currentSignUpType !== storedSignUpType) {
      const resetData = { ...initialSignUpFormState, signUpType: currentSignUpType };
      setSignUpFormData(resetData);
      setMemberInfo(resetData as MemberInfo);
      setIsFirst(true);
    } else {
      setMemberInfo(signUpFormData);
    }
  }, [isDealerSignUp, signUpFormData]);

  const handleClickClosePopup = () => {
    setIsShowPopup(false);
  };

  const goToDealerSignUp = () => {
    setIsShowDealerBlackListPopup(false);
    navigate('/signup', { replace: true, state: { signUpType: 'DEALER' } });
  };

  useEffect(() => {
    if (errorCode === DEALER_BLACK_LIST_MEMBER) {
      setIsShowDealerBlackListPopup(true);
      searchParams.delete('code');
      searchParams.delete('errorCode');
      searchParams.delete('errorMessage');
      setSearchParams(searchParams, { replace: true });
      return;
    }

    if (code === 'KAKAO_FAIL' && errorCode !== 'DEALER_SOCIAL_LOGIN_NOT_ALLOWED') {
      console.log(code);
      setIsShowPopup(true);
      searchParams.delete('code');
      setSearchParams(searchParams, { replace: true });
    } else if (code === 'KAKAO_FAIL' && errorCode === 'DEALER_SOCIAL_LOGIN_NOT_ALLOWED') {
      navigate('/login', {
        replace: true,
        state: {
          socialErrorMessage: '딜러 회원은 딜러회원 탭에서 전화번호만 로그인 가능합니다.',
        },
      });
    }
  }, [code, errorCode]);

  return (
    <>
      {isFirst ? (
        <PhoneAuthAndTerms
          memberInfo={memberInfo}
          updateMemberInfo={(data) => updateMemberInfo(data)}
          isFirst={isFirst}
          setIsFirst={setIsFirst}
          isDealerSignUp={isDealerSignUp}
        ></PhoneAuthAndTerms>
      ) : (
        <UserRegistrationForm
          memberInfo={memberInfo}
          updateMemberInfo={(data) => updateMemberInfo(data)}
          isFirst={isFirst}
          setIsFirst={setIsFirst}
          isDealerSignUp={isDealerSignUp}
        ></UserRegistrationForm>
      )}

      <AlertModal
        isOpen={isShowPopup}
        title="카카오 로그인 실패"
        content={
          <>
            카카오 계정에 등록된 <br />
            휴대폰 번호가 없습니다.
            <br />
            전화번호로 회원가입 해주세요.
          </>
        }
        onClose={handleClickClosePopup}
      />

      <AlertModal
        isOpen={isShowDealerBlackListPopup}
        title="딜러 회원 안내"
        content={<div className="break-keep whitespace-pre-line">{DEALER_BLACK_LIST_MEMBER_MESSAGE}</div>}
        buttonLabel="딜러 회원가입 하기"
        onClose={goToDealerSignUp}
      />
    </>
  );
};

export default SignUp;
